import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TermsConditionSectionProps } from "_interfaces/terms-conditions";
import { useEffect, useRef, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";

const SCROLL_OFFSET = 150;

const TermsConditionsContent = ({
  data,
}: {
  data: TermsConditionSectionProps[];
}) => {
  const { palette } = useTheme();
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const isManualScroll = useRef(false);

  const [activeTab, setActiveTab] = useState<number | null>(
    data?.[0]?.id ?? null
  );

  const handleTabClick = (id: number) => {
    isManualScroll.current = true;
    setActiveTab(id);

    const section = sectionRefs.current[id];
    section?.scrollIntoView({ behavior: "smooth", block: "start" });

    setTimeout(() => {
      isManualScroll.current = false;
    }, 700);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isManualScroll.current) return;

      for (const item of data) {
        if (item.id === undefined) continue;

        const section = sectionRefs.current[item.id];
        if (!section) continue;

        const rect = section.getBoundingClientRect();
        if (rect.top <= SCROLL_OFFSET && rect.bottom >= SCROLL_OFFSET) {
          setActiveTab(item.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [data]);

  return (
    <Box className="font-poppins">
      <Box
        sx={{
          position: "sticky",
          top: { xs: 84, sm: 89, lg: 65 },
          zIndex: 999,
          bgcolor: "background.paper",
          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
          borderRadius: 2,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value: number) => handleTabClick(value)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: palette.primary.main,
              height: 2,
            },
            "& .MuiTab-root": {
              textTransform: "uppercase",
              fontWeight: 500,
              color: palette.grey[700],
              minHeight: 48,
              fontSize: "0.8rem",
            },
            "& .Mui-selected": {
              color: palette.primary.main,
              fontWeight: 600,
              bgcolor: "rgba(255,152,0,0.08)",
              borderRadius: 1,
            },
          }}
        >
          {data.map(
            (item) =>
              item.id !== undefined && (
                <Tab key={item.id} label={item.Title} value={item.id} />
              )
          )}
        </Tabs>
      </Box>

      <Box mt={2}>
        {data.map(
          (item) =>
            item.id !== undefined && (
              <Box key={item.id} sx={{ scrollMarginTop: "150px", mb: 1 }}>
                <Accordion
                  expanded={activeTab === item.id}
                  onChange={() => {
                    if (activeTab === item.id) {
                      setActiveTab(null);
                    } else if (typeof item.id === "number") {
                      handleTabClick(item.id);
                    }
                  }}
                  sx={{
                    backgroundColor:
                      activeTab === item.id ? palette.grey[100] : "transparent",
                    boxShadow: "none",
                    border: "none",
                    borderRadius: 2,
                    transition: "background-color 0.3s ease",

                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          border: `2px solid ${palette.grey[700]}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </Box>
                    }
                    sx={{
                      fontWeight: 600,
                      fontSize: 16,
                      "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                        transform: "rotate(180deg)",
                      },
                    }}
                  >
                    {item.Title}
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box
                      sx={{ li: { lineHeight: 1.6, py: 0.8 } }}
                      dangerouslySetInnerHTML={{
                        __html: item.Description || "",
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            )
        )}
      </Box>
    </Box>
  );
};

export default TermsConditionsContent;
